import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

const AbleLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Dark */}
      <div className="hidden md:flex md:w-[42%] bg-[#0B1120] flex-col justify-between p-12 relative overflow-hidden">
        {/* Subtle lime accent */}
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#C6F135] rounded-full opacity-[0.07] blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-48 h-48 bg-[#C6F135] rounded-full opacity-[0.05] blur-2xl" />

        <div className="relative z-10">
          <img src="/able_logo_white.png" alt="Able" className="h-8 mb-16" />
          <h1 className="font-['Satoshi'] text-4xl font-bold text-white tracking-tight leading-tight">
            Welcome back
          </h1>
          <p className="mt-4 text-[#A1A1AA] text-lg leading-relaxed max-w-sm">
            Sign in to your account and pick up right where you left off.
          </p>
        </div>

        {/* Decorative dots */}
        <div className="relative z-10 flex space-x-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-60" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-30" />
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="md:hidden mb-10">
            <img src="/able-logo.svg" alt="Able" className="h-7 mb-6" />
          </div>

          <h2 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
            Sign in
          </h2>
          <p className="mt-2 text-gray-500 text-sm">
            Enter your credentials to access your account.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                placeholder="you@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 pr-11 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm font-medium text-[#2ECC71] hover:text-[#27AE60] transition-colors">
                Forgot password?
              </Link>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-[0.75rem] px-4 py-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group w-full flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ECC71] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center">
                  Sign in
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="mt-8 text-center text-sm text-gray-500">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-[#2ECC71] hover:text-[#27AE60] transition-colors">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AbleLogin;
